import { useMemo } from "react"
import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Bold from "@tiptap/extension-bold"
import Italic from "@tiptap/extension-italic"
import History from "@tiptap/extension-history"
import Collaboration from "@tiptap/extension-collaboration"
import CollaborationCaret from "@tiptap/extension-collaboration-caret"
import { AnyExtension } from "@tiptap/core"
import * as Y from "yjs"

import FontChangeExtension from "features/QuestionnaireEditor/components/Editor/Extensions/FontChangeExtension"
import CustomParagraph from "features/QuestionnaireEditor/components/Editor/Extensions/CustomParagraphExtension"
import SmartClipboardExtension from "shared/TipTap/Extensions/SmartClipboardExtension"
import CommentMark from "features/QuestionnaireEditor/components/Editor/Extensions/CommentMarkExtension"
import FormattingRestrictionExtension from "features/QuestionnaireEditor/components/Editor/Extensions/FormattingRestrictionExtension"
import RemarkExtension from "features/QuestionnaireEditor/components/Editor/Extensions/RemarkExtension"
import { IfTriggerExtension } from "features/QuestionnaireEditor/components/Editor/LogicWizard"
import { RecodeTriggerExtension } from "features/QuestionnaireEditor/components/Editor/RecodeWizard"

interface UseEditorExtensionsProps {
  highlightingEnabled: boolean
  isCollaborationReady: boolean
  collaborationEnabled: boolean
  currentDoc: Y.Doc | null
  currentVariant: string
  userId?: string
  getProvider: (variantName: string) => unknown
  /** Callback when a comment is clicked/activated in the editor */
  onCommentActivated?: (commentId: string | null) => void
  /** Whether comments feature is enabled */
  commentsEnabled?: boolean
}

// Collaboration cursor color palette
const CURSOR_COLORS = [
  "#36C3ED", // cyan
  "#F97316", // orange
  "#8B5CF6", // purple
  "#10B981", // emerald
  "#EC4899", // pink
  "#F59E0B", // amber
  "#6366F1", // indigo
  "#14B8A6", // teal
  "#EF4444", // red
  "#84CC16", // lime
]

/**
 * Get a consistent color for a user based on their ID
 */
const getUserColor = (userId: string): string => {
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash)
  }
  const index = Math.abs(hash) % CURSOR_COLORS.length
  return CURSOR_COLORS[index]
}

/**
 * Hook to configure TipTap editor extensions
 */
export const useEditorExtensions = ({
  highlightingEnabled,
  isCollaborationReady,
  collaborationEnabled,
  currentDoc,
  currentVariant,
  userId,
  getProvider,
  onCommentActivated,
  commentsEnabled = true,
}: UseEditorExtensionsProps): AnyExtension[] => {
  return useMemo(() => {
    const baseExtensions: AnyExtension[] = [
      Document,
      Text,
      FormattingRestrictionExtension, // Must come before Bold/Italic to intercept shortcuts
      RemarkExtension,
      IfTriggerExtension,
      RecodeTriggerExtension,
      Bold,
      Italic,
      CustomParagraph,
      SmartClipboardExtension,
      ...(highlightingEnabled
        ? [
            FontChangeExtension.configure({
              largeFileThreshold: 150000,
            }),
          ]
        : []),
      // Add CommentMark extension for inline comments
      ...(commentsEnabled
        ? [
            CommentMark.configure({
              onCommentActivated,
            }),
          ]
        : []),
    ]

    // Add collaboration extensions when ready
    if (isCollaborationReady && currentDoc && collaborationEnabled) {
      const provider = getProvider(currentVariant === "" ? "master" : currentVariant)

      const collaborationExtensions: AnyExtension[] = [
        Collaboration.configure({
          document: currentDoc,
        }),
      ]

      // Add CollaborationCaret for real-time cursor awareness
      if (provider && userId) {
        collaborationExtensions.push(
          CollaborationCaret.configure({
            provider,
            user: {
              name: userId,
              color: getUserColor(userId),
            },
            render: (user: { name: string; color: string }) => {
              const cursor = document.createElement("span")
              cursor.classList.add("collaboration-caret")
              cursor.style.setProperty("--caret-color", user.color)

              const flag = document.createElement("div")
              flag.classList.add("collaboration-caret__flag")

              const label = document.createElement("span")
              label.classList.add("collaboration-caret__label")
              label.textContent = user.name

              flag.appendChild(label)
              cursor.appendChild(flag)

              return cursor
            },
            selectionRender: (user: { color: string }) => ({
              class: "collaboration-selection",
              style: `background-color: ${user.color}; opacity: 0.4; pointer-events: none;`,
            }),
          })
        )
      }

      return [...baseExtensions, ...collaborationExtensions]
    }

    // Non-collaborative mode: include History
    return [...baseExtensions, History]
  }, [highlightingEnabled, isCollaborationReady, collaborationEnabled, getProvider, currentVariant, userId, currentDoc, onCommentActivated, commentsEnabled])
}
